import { formatDate } from 'date-fns';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogActions, DialogHeader, DialogContent } from '~/components/ui/Dialog';
import { Input } from '~/components/ui/Input';
import { deleteOrganization } from '~/lib/.client/api/organizations';
import { WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import { menuStore } from '~/lib/stores/menu';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { PricingPageOpenedTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';

interface Props {
  open: boolean;
  onClose: () => void;
  org: OrganizationData;
}

export const DeleteTeamDialog = ({ open, onClose, org }: Props) => {
  const hasActiveSubscription = org?.billing?.active && org.billing?.subscription?.status === 'active';

  if (hasActiveSubscription) {
    return <ActiveSubscriptionDialog open={open} onClose={onClose} org={org} />;
  }

  return <DeletableTeamDialog open={open} onClose={onClose} org={org} />;
};

const ActiveSubscriptionDialog = ({ open, onClose, org }: Props) => {
  function handleManageSubscriptionClick() {
    onClose();
    menuStore.closeDialog();
    pricingStore.openPricingOverlay(PricingPageOpenedTrigger.SettingsDeleteTeamModal);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={600}>
      <DialogHeader title="This team has an active subscription" noBorder />

      <DialogContent className="text-base text-bolt-ds-textPrimary">
        To delete <span className="font-bold">{org.name}</span>, you need to cancel the current subscription first.
      </DialogContent>

      <DialogActions>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleManageSubscriptionClick}>Manage subscription</Button>
      </DialogActions>
    </Dialog>
  );
};

const DeletableTeamDialog = ({ open, onClose, org }: Props) => {
  const [name, setName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const deletionAvailable = name === org.name;
  const hasCanceledSubscription = org?.billing?.active && org.billing?.subscription?.status === 'canceled';

  async function handleDeleteTeamClick() {
    setIsDeleting(true);

    await deleteOrganization({ org })
      .then(() => {
        toast.success('Your team has been deleted.');

        setTimeout(() => {
          window.location.reload();
        }, WINDOW_RELOAD_TIMEOUT);
      })
      .catch((err) => {
        toast.error(err.status === 409 ? 'Error: You must cancel your team subscription first' : err.message);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }

  function handleClose() {
    setName('');
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={600}>
      <DialogHeader title={`Delete ${org.name}`} badge="warning" noBorder />

      <DialogContent className="space-y-6 text-base text-bolt-ds-textPrimary">
        <p>
          Deleting this team is permanent and will remove all its <b className="text-bolt-ds-textPrimary">projects</b>,{' '}
          <b className="text-bolt-ds-textPrimary">members</b> and <b className="text-bolt-ds-textPrimary">data</b>.
        </p>

        {hasCanceledSubscription && org.billing?.subscription?.currentPeriodEnd && (
          <p>
            You can still used paid features until{' '}
            <b>{formatDate(org.billing.subscription.currentPeriodEnd, 'MMMM do, yyyy')}</b>
          </p>
        )}

        <Input placeholder="Enter your team name to confirm" value={name} onChange={(e) => setName(e.target.value)} />
      </DialogContent>

      <DialogActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="danger" onClick={handleDeleteTeamClick} disabled={!deletionAvailable} loading={isDeleting}>
          Delete now
        </Button>
      </DialogActions>
    </Dialog>
  );
};
